import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { flushSync } from "react-dom";

export type ThemeMode = "light" | "dark";
export type ThemePreference = ThemeMode | "material-dark";
export type ThemeTransitionOrigin = {
  x: number;
  y: number;
};
export type ThemeTransitionOptions = {
  origin?: ThemeTransitionOrigin;
};

const THEME_STORAGE_KEY = "choice-theme-mode";
const THEME_SWITCHING_CLASS = "theme-switching";
const THEME_VIEW_TRANSITION_CLASS = "theme-view-transition";
const THEME_SWITCH_DURATION_MS = 680;

type ThemeContextValue = {
  theme: ThemePreference;
  setTheme: (theme: ThemePreference, options?: ThemeTransitionOptions) => void;
  toggleTheme: (options?: ThemeTransitionOptions) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getStoredTheme(): ThemePreference | null {
  if (typeof window === "undefined") {
    return null;
  }

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (storedTheme === "light" || storedTheme === "dark" || storedTheme === "material-dark") {
    return storedTheme;
  }

  return null;
}

function getInitialTheme(): ThemePreference {
  if (typeof window === "undefined") {
    return "light";
  }

  const storedTheme = getStoredTheme();
  if (storedTheme) {
    return storedTheme;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(theme: ThemePreference) {
  document.documentElement.classList.toggle("dark", theme !== "light");
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme === "light" ? "light" : "dark";

  const themeColor = theme === "material-dark" ? "#121212" : theme === "dark" ? "#08111b" : "#221F20";
  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta) {
    themeColorMeta.setAttribute("content", themeColor);
  }
}

function beginCssThemeTransition() {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reducedMotion) {
    return () => undefined;
  }

  const root = document.documentElement;
  root.classList.add(THEME_SWITCHING_CLASS);

  const timeoutId = window.setTimeout(() => {
    root.classList.remove(THEME_SWITCHING_CLASS);
  }, THEME_SWITCH_DURATION_MS);

  return () => {
    window.clearTimeout(timeoutId);
    root.classList.remove(THEME_SWITCHING_CLASS);
  };
}

function getThemeTransitionOrigin(origin?: ThemeTransitionOrigin) {
  if (origin) {
    return origin;
  }

  return {
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  };
}

function beginThemeTransition(
  commit: () => void,
  options?: ThemeTransitionOptions,
) {
  if (typeof window === "undefined") {
    commit();
    return () => undefined;
  }

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const root = document.documentElement;

  if (!reducedMotion && typeof document.startViewTransition === "function") {
    const { x, y } = getThemeTransitionOrigin(options?.origin);
    root.style.setProperty("--theme-switch-origin-x", `${x}px`);
    root.style.setProperty("--theme-switch-origin-y", `${y}px`);
    root.classList.add(THEME_VIEW_TRANSITION_CLASS);

    const transition = document.startViewTransition(() => {
      flushSync(commit);
    });

    const cleanup = () => {
      root.classList.remove(THEME_VIEW_TRANSITION_CLASS);
      root.style.removeProperty("--theme-switch-origin-x");
      root.style.removeProperty("--theme-switch-origin-y");
    };

    void transition.finished.finally(cleanup);
    return cleanup;
  }

  const cleanup = beginCssThemeTransition();
  commit();
  return cleanup;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(getInitialTheme);
  const [hasExplicitPreference, setHasExplicitPreference] = useState(() => getStoredTheme() !== null);
  const transitionCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    applyTheme(theme);
    window.dispatchEvent(new CustomEvent("choice-theme-change", { detail: theme }));
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (hasExplicitPreference) {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
      return;
    }

    window.localStorage.removeItem(THEME_STORAGE_KEY);
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const syncTheme = (event?: MediaQueryListEvent) => {
      setThemeState((event?.matches ?? mediaQuery.matches) ? "dark" : "light");
    };

    syncTheme();
    mediaQuery.addEventListener("change", syncTheme);

    return () => {
      mediaQuery.removeEventListener("change", syncTheme);
    };
  }, [hasExplicitPreference, theme]);

  useEffect(() => {
    return () => {
      transitionCleanupRef.current?.();
      transitionCleanupRef.current = null;
    };
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme: (nextTheme, options) => {
        transitionCleanupRef.current?.();
        setHasExplicitPreference(true);
        transitionCleanupRef.current = beginThemeTransition(() => {
          setThemeState(nextTheme);
        }, options);
      },
      toggleTheme: (options) => {
        transitionCleanupRef.current?.();
        setHasExplicitPreference(true);
        transitionCleanupRef.current = beginThemeTransition(() => {
          setThemeState((current) => {
            if (current === "light") {
              return "dark";
            }

            if (current === "dark") {
              return "material-dark";
            }

            return "light";
          });
        }, options);
      },
    }),
    [theme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }

  return context;
}
